/**
 * studentService — Firestore reads/writes for the `students` collection.
 * Centralizes the inline calls previously in Students, StudentProfile, Today,
 * and Email Announcements. Behavior is unchanged; only the call sites moved.
 */

import { db } from '../firebase';
import {
	collection,
	doc,
	getDoc,
	getDocs,
	query,
	updateDoc,
	where,
} from 'firebase/firestore';

const COLLECTION = 'students';

// Whole roster (Students, Email Announcements). Caller does any
// mapping/filtering (e.g. lessonDay, active-only) to preserve prior behavior.
export async function fetchStudents() {
	const snapshot = await getDocs(query(collection(db, COLLECTION)));
	return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// A family's students (customer schedule view — Slice B). The where() is
// load-bearing, not an optimization: the customer read rule only authorizes
// `familyId == users.familyId`, so an unfiltered customer query is denied
// whole (rules are not filters). Never call with a null/empty familyId.
export async function fetchStudentsByFamily(familyId) {
	const snapshot = await getDocs(
		query(collection(db, COLLECTION), where('familyId', '==', familyId))
	);
	return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// One student by id, or null if not found (StudentProfile, Today lookups).
export async function fetchStudent(id) {
	const snap = await getDoc(doc(db, COLLECTION, id));
	return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// Apply a list of { id, data } field updates as INDEPENDENT writes so one
// rules-denied doc (e.g. a locked student) can't sink the rest. Returns
// { succeeded: [id], failed: [{ id, error }] } for partial-success reporting.
// (A single atomic writeBatch rolled ALL updates back on any one denial — the
// #17 bug: "Mark as Test Student" over a selection containing one locked
// student failed for every student.)
export async function commitStudentUpdates(updates) {
	const results = await Promise.allSettled(
		updates.map(({ id, data }) => updateDoc(doc(db, COLLECTION, id), data))
	);
	const succeeded = [];
	const failed = [];
	results.forEach((r, i) => {
		const { id } = updates[i];
		if (r.status === 'fulfilled') succeeded.push(id);
		else failed.push({ id, error: r.reason });
	});
	return { succeeded, failed };
}
